import React from 'react';
import { useTranslation } from 'react-i18next';
import { Change } from '../../models';
import { Icon } from '../Icon';
import { UserAvatar } from '../UserAvatar';
import { Tooltip } from '../Tooltip';
import RequesterEmails from '../RequesterEmails/RequesterEmails';
import { Badge, Progress } from 'flowbite-react';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';
import { requesterEmailsOf } from '../../utils/changeRequester';

interface ChangeListItemProps {
    change: Change;
    onSelect?: (change: Change, e: AnyObj) => void;
    onRemove?: (change: Change, e: AnyObj) => void;
    showProject?: boolean;
    /** Show the first demandeur email (search results). */
    showRequester?: boolean;
    showProgression?: boolean;
    selectable?: boolean;
    selected?: boolean;
    onToggleSelect?: (change: Change, e: React.MouseEvent) => void;
    className?: string;
    tabIndex?: number;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
}

function ChangeListItem(props: ChangeListItemProps) {
    const {
        change, onSelect, onRemove, showProject, showRequester, showProgression,
        selectable, selected, onToggleSelect, className,
    } = props;

    const { t, i18n } = useTranslation();

    const progression = Math.round(change.progression());
    const isDiscarded = change.progress?.status === Change.STATUS_DISCARDED;
    const firstRequester = showRequester ? requesterEmailsOf(change)[0] : undefined;

    return (
        <div
            className={`overflow-hidden min-h-[60px] border rounded p-2 cursor-pointer ${className ? className : ""} ${props.tabIndex === 0 ? "bg-blue-100 dark:bg-blue-900/40" : ""} ${selected ? "ring-2 ring-indigo-300" : ""} ${isDiscarded ? "change-list-item--discarded" : ""}`}
            tabIndex={props.tabIndex}
            style={{outline: "none"}}
            onClick={props.onClick}
            onMouseDown={preventMiddleClickAutoscroll}
            onAuxClick={(event) => handleChangeMiddleClick(event, change)}
        >
            <div className={`flex flex-row items-center space-x-2`}>
                {selectable && (
                    <input
                        type="checkbox"
                        checked={!!selected}
                        onChange={() => {}}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleSelect?.(change, e);
                        }}
                        className="h-4 w-4 shrink-0 cursor-pointer"
                        aria-label={t('priorities.bulk_actions') || ''}
                    />
                )}
                <div className="flex-col space-y-0.5">
                    <Badge color={change.color()} className="w-[70px] flex-none text-xs">
                        <Icon type={change.iconName()} size={3} color="inherit" className="inline"/>&nbsp;
                        {change.slugName()}
                    </Badge>
                    <Tooltip content={t('changes.help.estimate') || ''} light>
                        <Badge color="warning" className="w-[70px] flex-none whitespace-nowrap">
                            <>
                                <Icon type="estimated" size={3} color="inherit" className="inline mr-1"/>&nbsp;
                                {change.estimate} {t('changes.estimate_units_min')}
                            </>
                        </Badge>
                    </Tooltip>
                </div>
                <div className="flex-1">
                    <h4 className="text-sm font-light heading-md">
                        {change.localizedTitle(i18n.language)}
                    </h4>
                    {showProject && (
                        <h4 className="text-xs font-bold heading-md">
                            {change.project?.name}
                        </h4>
                    )}
                    {firstRequester && (
                        <h4 className="text-xs text-gray-500 dark:text-gray-400 heading-md truncate">
                            <RequesterEmails
                                emails={[firstRequester]}
                                empty={null}
                                className="truncate"
                                linkClassName="text-gray-500 dark:text-gray-400 hover:underline hover:text-indigo-600 dark:hover:text-indigo-300"
                            />
                        </h4>
                    )}
                    {change.ticket_number && (
                        <h4 className="text-xs text-gray-500 dark:text-gray-400 heading-md">
                            RT #{change.ticket_number}
                        </h4>
                    )}
                </div>
                {isDiscarded && (
                    <Badge color="warning" className="w-fit" size="xs">
                        <Icon type="delete" color="inherit" className="inline mr-1" size={3} />
                        {t('changes.progress_status_type.discarded')}
                    </Badge>
                )}
                {change.isFinished() && (
                    <Icon type="check" color="green-600" size={6} />
                )}
                {change.isInReview() && (
                    <Icon type="eye" color="blue-600" size={6} />
                )}
                <Tooltip content={t('changes.help.priority') || ''} light>
                    <Badge color="indigo" className="w-fit" size="xs">
                        <Icon type="priority" color="inherit" className="inline mr-1" />
                        {change.priority}
                    </Badge>
                </Tooltip>
                {change.assignee && change.assignee._id && (
                    <UserAvatar user={change.assignee} size={6} />
                )}
                {onSelect && (
                    <Badge
                        color="success"
                        className="text-xs"
                        onClick={(e: AnyObj) => {
                            e.stopPropagation();
                            onSelect(change, e);
                        }}
                    >
                        <Icon type="right" color="inherit" className="" />
                    </Badge>
                )}
                {onRemove && (
                    <Badge
                        color="failure"
                        className="text-xs"
                        onClick={(e: AnyObj) => {
                            e.stopPropagation();
                            onRemove(change, e);
                        }}
                    >
                        <Icon type="close" color="inherit" className="" />
                    </Badge>
                )}
            </div>
            {showProgression && (
                <Tooltip content={progression + '%'} placement="top">
                    <div className="mt-1">
                        <Progress
                            progress={progression}
                            size="sm"
                            color={ progression === 100 ? 'green' : (progression > 20 ? 'blue' : 'yellow')}
                        />
                    </div>
                </Tooltip>
            )}
        </div>
    );
}

export default ChangeListItem;
